import { Embedded, EmbeddedFactory, toFactoryEmbedded } from './embed.model';

export interface OpportunityDocument {
    name: string;
    url: string;
}

interface EmbedProperties {
    $factory: EmbeddedFactory;
}

export type EmbedQuoteLineItem = QuoteLineItem & EmbedProperties;
export type EmbedQuoteGroup = QuoteGroup & EmbedProperties & { quoteLineItems: EmbedQuoteLineItem[] };
export type EmbedOpportunity = Omit<Opportunity, 'groups'> & { groups: EmbedQuoteGroup[] };

export class QuoteLineItem {
    public productLabel: string;
    public productId: string;
    public id: string;
    public unit: string;
    public quantity: number;
    public factory: string;
    public notes: string;
    public deltaQuantity: number;
    public parentId?: string;
    public jiraCardId?: string;

    public constructor(data: Partial<QuoteLineItem>) {
        this.productLabel = data.productLabel || '';
        this.productId = data.productId || '';
        this.id = data.id || '';
        this.unit = data.unit || '';
        this.quantity = data.quantity || 0;
        this.factory = data.factory || '';
        this.notes = data.notes || '';
        this.deltaQuantity = data.deltaQuantity || 0;
        this.parentId = data.parentId;
        this.jiraCardId = data.jiraCardId;
    }

    public static toQuoteLineEmbedded(data: QuoteLineItem, embedded?: Embedded): EmbedQuoteLineItem {
        const factory = toFactoryEmbedded(data, 'factory', embedded);
        return {
            ...data,
            $factory: factory || { id: '', name: '' }
        };
    }
}

export class QuoteGroup {
    public groupId: string;
    public name: string;
    public deliveryDate: Date;
    public startDate: Date;
    public quoteLineItems: QuoteLineItem[];
    public factoryGroup: string;
    public billingType: BillingType;
    public billingTrigger: BillingTrigger;
    public notes: string;
    public jiraCardId?: string;

    public constructor(data: Partial<QuoteGroup>) {
        this.groupId = data.groupId || '';
        this.name = data.name || '';
        this.factoryGroup = data.factoryGroup || '';
        this.notes = data.notes || '';
        this.billingType = data.billingType || BillingType.UNKNOWN_BILLING_TYPE;
        this.billingTrigger = data.billingTrigger || BillingTrigger.UNKNOWN_BILLING_TRIGGER;
        this.deliveryDate = data.deliveryDate ? new Date(data.deliveryDate) : null;
        this.startDate = data.startDate ? new Date(data.startDate) : null;
        this.quoteLineItems = data.quoteLineItems ? data.quoteLineItems.map((line) => new QuoteLineItem(line)) : [];
        this.jiraCardId = data.jiraCardId;
    }

    public static toQuoteGroupEmbedded(data: QuoteGroup, embedded?: Embedded): EmbedQuoteGroup {
        const factory = toFactoryEmbedded(data, 'factoryGroup', embedded);
        return {
            ...data,
            quoteLineItems: data.quoteLineItems.map(item => QuoteLineItem.toQuoteLineEmbedded(item, embedded)),
            $factory: factory || { id: '', name: '' }
        };
    }
}

export class Opportunity {
    public name: string;
    public createdAt: Date;
    public createdBy: string;
    public quoteId: string;
    public quoteName: string;
    public link: string;
    public businessProject: string;
    public opportunityType: OpportunityType;
    public documents: OpportunityDocument[];
    public subscriptionLength: number;
    public subscriptionStart: Date;
    public subscriptionEnd: Date;
    public terminationDate: Date;
    public terminationReason: string;
    public groups: QuoteGroup[];
    public cancellationDate: Date;

    constructor(data: Partial<Opportunity>) {
        this.name = data.name || '';
        this.quoteId = data.quoteId || '';
        this.quoteName = data.quoteName || '';
        this.link = data.link;
        this.opportunityType = data.opportunityType || OpportunityType.UNKNOWN_OPPORTUNITY_TYPE;
        this.documents = data.documents || [];
        this.businessProject = data.businessProject || '';
        this.subscriptionLength = data.subscriptionLength || 0;
        this.subscriptionStart = data.subscriptionStart ? new Date(data.subscriptionStart) : null;
        this.subscriptionEnd = data.subscriptionEnd ? new Date(data.subscriptionEnd) : null;
        this.terminationDate = data.terminationDate ? new Date(data.terminationDate) : null;
        this.terminationReason = data.terminationReason || '';
        this.groups = data.groups ? data.groups.map((group) => (new QuoteGroup(group))) : [];

        this.createdAt = data.createdAt ? new Date(data.createdAt) : null;
        this.createdBy = data.createdBy || '';

        this.cancellationDate = data.cancellationDate ? new Date(data.cancellationDate) : null;
    }

    public static toOpportunityEmbedded(data: Opportunity, embedded?: Embedded): EmbedOpportunity {
        return {
            ...data,
            groups: data.groups.map(group => QuoteGroup.toQuoteGroupEmbedded(group, embedded))
        };
    }
}


export enum OpportunityType {
    UNKNOWN_OPPORTUNITY_TYPE = 'UNKNOWN_OPPORTUNITY_TYPE',
    NEW = 'NEW',
    RENEW = 'RENEW',
    AMEND = 'AMEND',
    TERMINATION = 'TERMINATION',
}

export enum BillingType {
    UNKNOWN_BILLING_TYPE = 'UNKNOWN_BILLING_TYPE',
    RECURRING = 'RECURRING',
    ONE_SHOT = 'ONE_SHOT',
    USAGE_BASED = 'USAGE_BASED',
}

export enum BillingTrigger {
    UNKNOWN_BILLING_TRIGGER = 'UNKNOWN_BILLING_TRIGGER',
    ON_DOWN_PAYMENT = 'ON_DOWN_PAYMENT',
    ON_FIXED_DATE = 'ON_FIXED_DATE',
    ON_DELIVERY = 'ON_DELIVERY',
    ON_MILESTONE = 'ON_MILESTONE',
    ON_RELEASE = 'ON_RELEASE',
    ON_USAGE = 'ON_USAGE',
}
